import { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../../../helpers";
import styles from "./AiCreditBalance.module.scss";

const formatUnits = (value) => Number(value || 0).toLocaleString("en-US");

const formatNaira = (value) =>
  formatCurrency({ value: Number(value || 0), currencyCode: "NGN" });

const AiCreditBalance = ({ balance, isLoading, isError, onTopUp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) setIsOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // A balance we could not fetch is hidden outright — it must never get in the
  // way of chatting.
  if (isError) return null;

  if (isLoading)
    return (
      <div
        className={`${styles.aiCredit__pill} ${styles["aiCredit__pill--skeleton"]}`}
        aria-hidden="true"
      />
    );

  if (!balance) return null;

  const freeUnits = Number(balance.free_units || 0);
  const mainUnits = Number(balance.main_units || 0);
  const freeUnitsPerDay = Number(balance.free_units_per_day || 0);
  const outstanding = Number(balance.outstanding_amount || 0);
  const isDepleted = freeUnits === 0 && mainUnits === 0;
  const freeUsedPercent = freeUnitsPerDay
    ? Math.min(100, Math.max(0, (freeUnits / freeUnitsPerDay) * 100))
    : 0;

  return (
    <div className={styles.aiCredit} ref={containerRef}>
      <button
        type="button"
        className={`${styles.aiCredit__pill} ${
          isDepleted ? styles["aiCredit__pill--warning"] : ""
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title="AI credit balance"
      >
        <span className={styles.aiCredit__pillItem}>
          <span className={styles.aiCredit__pillLabel}>Free</span>
          {formatUnits(freeUnits)}
        </span>
        <span className={styles.aiCredit__pillDot} aria-hidden="true" />
        <span className={styles.aiCredit__pillItem}>
          <span className={styles.aiCredit__pillLabel}>Main</span>
          {formatUnits(mainUnits)}
        </span>
      </button>

      {isOpen && (
        <div className={styles.aiCredit__popover} role="dialog">
          <div className={styles.aiCredit__section}>
            <div className={styles.aiCredit__sectionHead}>
              <span className={styles.aiCredit__sectionTitle}>Free credit</span>
              <span className={styles.aiCredit__sectionValue}>
                {formatUnits(freeUnits)} of {formatUnits(freeUnitsPerDay)} left
              </span>
            </div>
            <div className={styles.aiCredit__progress}>
              <span style={{ width: `${freeUsedPercent}%` }} />
            </div>
            <p className={styles.aiCredit__hint}>Resets daily</p>
          </div>

          <div className={styles.aiCredit__section}>
            <div className={styles.aiCredit__sectionHead}>
              <span className={styles.aiCredit__sectionTitle}>Main credit</span>
              <span className={styles.aiCredit__sectionValue}>
                {formatUnits(mainUnits)} units
              </span>
            </div>
            <p className={styles.aiCredit__hint}>
              {formatNaira(balance.spendable_balance)} of credit
            </p>
            {outstanding > 0 && (
              <p className={styles.aiCredit__hint}>
                {formatNaira(outstanding)} of usage pending settlement
              </p>
            )}
          </div>

          {balance.price_per_1000_units && (
            <p className={styles.aiCredit__rate}>
              ≈ {formatNaira(balance.price_per_1000_units)} per 1,000 units
            </p>
          )}

          <button
            type="button"
            className={styles.aiCredit__topUp}
            onClick={() => {
              setIsOpen(false);
              onTopUp();
            }}
          >
            Top up credit
          </button>
        </div>
      )}
    </div>
  );
};

export default AiCreditBalance;
